/**
 * Compute non-transparent (alpha) content bounds for a PNG/WebP with transparency.
 * Returns bounds normalized to the image (0..1), plus scale factors useful for VTO.
 *
 * This is used to correct for transparent padding that would otherwise make frames/temples
 * appear smaller than intended and shift hinge placement.
 */

const DEFAULT_ALPHA_THRESHOLD = 20; // 0..255
const DEFAULT_MAX_SCAN_SIZE = 512; // downscale for speed

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function loadImage(dataUrlOrUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrlOrUrl;
  });
}

export function computeAlphaBoundsFromImage(img, opts = {}) {
  const { alphaThreshold = DEFAULT_ALPHA_THRESHOLD, maxScanSize = DEFAULT_MAX_SCAN_SIZE } = opts;
  const w0 = img?.naturalWidth || img?.width || 0;
  const h0 = img?.naturalHeight || img?.height || 0;
  if (!w0 || !h0) {
    return {
      ok: false,
      reason: 'invalid_image_dimensions',
      xMin: 0, yMin: 0, xMax: 1, yMax: 1,
      scaleX: 1, scaleY: 1,
      centerX: 0.5, centerY: 0.5,
      widthPx: 0, heightPx: 0,
      imageWidth: w0, imageHeight: h0,
    };
  }

  const scale = Math.min(1, maxScanSize / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return {
      ok: false,
      reason: 'no_canvas_context',
      xMin: 0, yMin: 0, xMax: 1, yMax: 1,
      scaleX: 1, scaleY: 1,
      centerX: 0.5, centerY: 0.5,
      widthPx: 0, heightPx: 0,
      imageWidth: w0, imageHeight: h0,
    };
  }

  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  let data;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch (e) {
    // Likely CORS-tainted canvas for remote URLs without CORS headers.
    return {
      ok: false,
      reason: 'tainted_canvas',
      xMin: 0, yMin: 0, xMax: 1, yMax: 1,
      scaleX: 1, scaleY: 1,
      centerX: 0.5, centerY: 0.5,
      widthPx: 0, heightPx: 0,
      imageWidth: w0, imageHeight: h0,
    };
  }

  let minX = w, minY = h, maxX = -1, maxY = -1;
  const thr = Math.max(0, Math.min(255, alphaThreshold | 0));

  // Scan alpha channel only
  for (let y = 0; y < h; y++) {
    const row = y * w * 4;
    for (let x = 0; x < w; x++) {
      const a = data[row + x * 4 + 3];
      if (a > thr) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  // If no opaque pixels found, fallback to full image
  if (maxX < 0 || maxY < 0) {
    return {
      ok: false,
      reason: 'no_opaque_pixels',
      xMin: 0, yMin: 0, xMax: 1, yMax: 1,
      scaleX: 1, scaleY: 1,
      centerX: 0.5, centerY: 0.5,
      widthPx: 0, heightPx: 0,
      imageWidth: w0, imageHeight: h0,
    };
  }

  // Convert from scan-space pixels to normalized coordinates in original image space.
  // +1 on max to make it inclusive->exclusive bounds.
  const xMin = clamp01(minX / w);
  const yMin = clamp01(minY / h);
  const xMax = clamp01((maxX + 1) / w);
  const yMax = clamp01((maxY + 1) / h);
  const scaleX = clamp01(xMax - xMin);
  const scaleY = clamp01(yMax - yMin);

  return {
    ok: true,
    reason: null,
    xMin,
    yMin,
    xMax,
    yMax,
    scaleX,
    scaleY,
    centerX: xMin + scaleX / 2,
    centerY: yMin + scaleY / 2,
    widthPx: Math.max(0, maxX - minX + 1),
    heightPx: Math.max(0, maxY - minY + 1),
    imageWidth: w0,
    imageHeight: h0,
  };
}

export async function computeAlphaBoundsFromDataUrl(dataUrlOrUrl, opts = {}) {
  const img = await loadImage(dataUrlOrUrl);
  return computeAlphaBoundsFromImage(img, opts);
}

